import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Badge } from '@/design-system/badge';
import { Button } from '@/design-system/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/design-system/dropdown-menu';
import { FormModal } from '@/design-system/form-modal';
import { toast } from '@/design-system/toaster';
import {
  BatchReassignMode,
  BatchStatus,
  DispatchDetail,
  DispatchDetailBatch,
  DispatchDetailItem,
  DispatchDetailStop,
  DispatchDisposition,
  DispatchRiderSnapshot,
  RefreshDispatchStrategy,
  RiderReassignMode,
  UnscannedItemsError,
  dispatchesAPI,
  tripKindOf,
} from '@/services/api/dispatches';
import { Hub, hubsAPI } from '@/services/api/hubs';
import { formatDateTime } from '@/utils';

import {
  AutoDispatchBadge,
  DispatchStatusBadge,
  UnscannedBadge,
} from '../components/DispatchBadges';

import AddRidersModal from './AddRidersModal';
import AssignDeferredModal from './AssignDeferredModal';
import BatchCard from './BatchCard';
import ForceAssignModal from './ForceAssignModal';
import CancelItemModal from './CancelItemModal';
import CancelStopItemsModal from './CancelStopItemsModal';
import CommitDispatchModal from './CommitDispatchModal';
import DeferredSection from './DeferredSection';
import DispatchMap, { batchColor } from './DispatchMap';
import MergeTripModal from './MergeTripModal';
import MoveStopModal from './MoveStopModal';
import ReassignBatchModal from './ReassignBatchModal';
import ReassignBatchesModal from './ReassignBatchesModal';
import ReassignRiderModal from './ReassignRiderModal';
import RefreshDispatchModal from './RefreshDispatchModal';

// Batch statuses the map + rider sidebar should draw. PLANNED = pre-commit
// preview; DISPATCHED = post-commit (DS bridge succeeded) — we keep showing
// these so the routes/items stay visible on the map after a dispatch is
// committed. FAILED belongs here too: the bridge errored, so those parcels are
// still sitting at the hub and ops must be able to see exactly which trip,
// which stops and which waybills didn't go out (they'd otherwise vanish from
// the page entirely, leaving only a PARTIAL badge in the header).
const MAP_VISIBLE_BATCH_STATUSES: ReadonlySet<BatchStatus> = new Set([
  'PLANNED',
  'DISPATCHED',
  'FAILED',
] as BatchStatus[]);

// ─── format helpers ───────────────────────────────────────────────────────
// BE defer-reason codes → ops-readable copy. Unknown codes fall through raw.
const DEFER_REASON_LABELS: Record<string, string> = {
  NO_RIDER_CAPACITY:
    'belum kebagian rider — kapasitas / waktu shift rider tidak cukup',
};
const deferReasonLabel = (code: string | null): string | null =>
  code ? DEFER_REASON_LABELS[code] ?? code : null;

// Thin adapters onto the sonner toaster so the many call sites below keep
// their title + description copy verbatim (react-toastify is retired).
const showSuccessToast = (title: string, description?: string) =>
  toast.success(title, description ? { description } : undefined);
const showErrorToast = (title: string, description?: string) =>
  toast.error(title, description ? { description } : undefined);

const fmtWeight = (kg: number) => `${kg.toFixed(2)} kg`;
const fmtVolume = (m3: number) => `${m3.toFixed(3)} m³`;
const fmtMinutes = (min: number) => {
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};

// ─── row helper ───────────────────────────────────────────────────────────
const DetailRow: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }) => (
  <div className="flex flex-col gap-0.5">
    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-text-soft-400">
      {label}
    </p>
    <div className="text-[13px] font-medium text-text-strong-950 leading-snug">
      {children}
    </div>
  </div>
);

// One context chip on the header's third row (hub, mode, client, riders):
// quiet label-sized pill, icon leading. `accent` marks the client scope.
const HeaderChip: React.FC<{
  icon: string;
  tone?: 'neutral' | 'accent';
  children: React.ReactNode;
}> = ({ icon, tone = 'neutral', children }) => (
  <span
    className={`inline-flex h-[22px] items-center gap-1.5 rounded-md px-2 text-[11px] font-semibold ${
      tone === 'accent'
        ? 'bg-accent/10 text-accent'
        : 'bg-bg-weak-50 text-text-sub-600'
    }`}
  >
    <i
      className={`${icon} text-[12px] ${
        tone === 'accent' ? 'text-accent' : 'text-text-soft-400'
      }`}
      aria-hidden="true"
    />
    {children}
  </span>
);

// Single dispatch-wide stat shown in the strip below the header. Tight
// label-over-value pair, optimized for at-a-glance scanning across the
// row (Items · Stops · Batches · …). `tone='warn'` paints the value
// amber for things that need attention (oversized count, etc.).
const StatCell: React.FC<{
  label: string;
  value: React.ReactNode;
  tone?: 'default' | 'warn' | 'defer' | 'error';
}> = ({ label, value, tone = 'default' }) => {
  const toneClass =
    tone === 'warn'
      ? 'text-amber-600'
      : tone === 'defer'
      ? 'text-orange-600'
      : tone === 'error'
      ? 'text-red-600'
      : 'text-text-strong-950';
  return (
    <div className="flex flex-col gap-0.5">
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-text-soft-400">
        {label}
      </p>
      <p className={`text-[15px] font-bold leading-none tabular-nums ${toneClass}`}>
        {value}
      </p>
    </div>
  );
};

// Sortable wrapper for one batch/trip card inside a rider group. Owns the
// sortable node + the grip button (the only drag activator, so the card's
// click/expand keeps working) and hands the grip to BatchCard via a render
// prop. `innerRef` keeps feeding batchRefsMap for scroll-into-view.
const SortableBatchRow: React.FC<{
  batchId: string;
  showHandle: boolean;
  disabled?: boolean;
  innerRef: (el: HTMLDivElement | null) => void;
  children: (dragHandle: React.ReactNode) => React.ReactNode;
}> = ({ batchId, showHandle, disabled, innerRef, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: batchId, disabled: disabled || !showHandle });
  const handle = showHandle ? (
    <button
      type="button"
      ref={setActivatorNodeRef}
      disabled={disabled}
      onClick={(e) => e.stopPropagation()}
      aria-label="Geser untuk mengubah urutan trip"
      title="Geser untuk mengubah urutan trip"
      className="grid h-6 w-6 shrink-0 cursor-grab touch-none place-items-center rounded-md text-text-soft-400 transition-colors hover:bg-bg-weak-50 hover:text-text-sub-600 active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-50"
      {...attributes}
      {...listeners}
    >
      <i className="ri-draggable text-[14px]" aria-hidden="true" />
    </button>
  ) : null;
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={isDragging ? 'relative z-10' : undefined}
    >
      <div ref={innerRef}>{children(handle)}</div>
    </div>
  );
};

// ─── page ─────────────────────────────────────────────────────────────────
const DispatchDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [dispatch, setDispatch] = useState<DispatchDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [hub, setHub] = useState<Hub | null>(null);
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
  // Per-rider visibility on the map. Hidden riders stay in the sidebar
  // (greyed) so ops can find them again to re-show. Session-scoped: a
  // refresh resets the filter, so stale hides don't follow ops across
  // dispatches.
  const [hiddenRiderIds, setHiddenRiderIds] = useState<Set<number>>(
    () => new Set()
  );
  // Solo-rider mode: when set, the map shows ONLY this rider's batches.
  // Triggered by clicking a rider header in the sidebar (toggles off when
  // the same rider is clicked again, or when a different rider is clicked
  // — see toggleRiderExpanded). Overrides hiddenRiderIds while active.
  const [soloRiderId, setSoloRiderId] = useState<number | null>(null);
  // Which rider groups are expanded in the accordion. Default closed so 20
  // riders all fit in the sidebar without scrolling. Map marker clicks
  // auto-expand the owning rider via the effect below.
  const [expandedRiderIds, setExpandedRiderIds] = useState<Set<number>>(
    () => new Set()
  );
  // DOM refs per batch card so we can scroll the sidebar to a batch when
  // its marker is clicked on the map (two-way binding).
  const batchRefsMap = useRef<Map<string, HTMLDivElement>>(new Map());

  // PATCH action target — stop-level reassign opens this modal.
  const [reassignStopId, setReassignStopId] = useState<string | null>(null);
  // Rider-level reassign (transfer/swap all batches) — source rider, or null.
  const [reassignRiderSource, setReassignRiderSource] =
    useState<DispatchRiderSnapshot | null>(null);
  // Batch-level reassign (transfer/swap one batch) — target batch, or null.
  const [reassignBatchTarget, setReassignBatchTarget] =
    useState<DispatchDetailBatch | null>(null);
  // Assign held (DEFERRED) stops onto a rider — the batch + which of its stops
  // are moving (one, or all). Null when the assign modal is closed.
  const [assignDeferredTarget, setAssignDeferredTarget] = useState<{
    batch: DispatchDetailBatch;
    stops: DispatchDetailStop[];
  } | null>(null);
  // Move-one-stop-to-another-trip target — the stop + its current batch (we
  // keep the whole batch so we can scope targets to the same rider's trips).
  // Null when the move modal is closed.
  const [moveStopTarget, setMoveStopTarget] = useState<{
    stop: DispatchDetailStop;
    batch: DispatchDetailBatch;
  } | null>(null);
  // Merge-trip source — the batch whose stops get folded into another of the
  // same rider's trips. Null when the merge modal is closed.
  const [mergeTripSource, setMergeTripSource] =
    useState<DispatchDetailBatch | null>(null);
  const [committing, setCommitting] = useState(false);
  const [commitOpen, setCommitOpen] = useState(false);
  // Last commit rejection by the shipment-complete gate (§D10) — rendered
  // inside the commit modal so ops sees which barang to scan / mark hilang.
  const [unscannedError, setUnscannedError] =
    useState<UnscannedItemsError | null>(null);
  // Cancel-stop-items (§D4) — target stop id, or null when closed.
  const [cancelStopId, setCancelStopId] = useState<string | null>(null);
  // Cancel a single item — target item, or null when the confirm modal closed.
  const [cancelItemTarget, setCancelItemTarget] =
    useState<DispatchDetailItem | null>(null);
  // Add-riders (§D5) modal toggle.
  const [addRidersOpen, setAddRidersOpen] = useState(false);
  // Force-assign (oversized rescue) modal toggle.
  const [forceAssignOpen, setForceAssignOpen] = useState(false);
  // Pull-arrivals refresh (§D12). `refreshingPlan` also disables the trigger
  // button — two concurrent REPLACE_ALL refreshes can lost-update on the BE.
  const [refreshPlanOpen, setRefreshPlanOpen] = useState(false);
  // Run details (caps, time model, timestamps) — opened from the ⋯ menu.
  const [runDetailsOpen, setRunDetailsOpen] = useState(false);
  // The failed-batch alert card; the title-row "Bridge gagal" badge scrolls here.
  const failedCardRef = useRef<HTMLDivElement>(null);
  const [refreshingPlan, setRefreshingPlan] = useState(false);
  // Per-dispatch CSV export (TRD §4.9) — blob download, so only a spinner.
  const [exporting, setExporting] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  // Look up the rider currently owning the to-be-reassigned stop so the modal
  // can highlight "current" and disable the no-op selection.
  const reassignSourceRiderId = useMemo<number | null>(() => {
    if (!dispatch || !reassignStopId) return null;
    for (const b of dispatch.batches) {
      if (b.stops.some((s) => s.id === reassignStopId)) {
        return b.assignedRiderId ?? null;
      }
    }
    return null;
  }, [dispatch, reassignStopId]);

  // Full stop object for the cancel-items modal (needs the item list).
  const cancelStopTarget = useMemo<DispatchDetailStop | null>(() => {
    if (!dispatch || !cancelStopId) return null;
    for (const b of dispatch.batches) {
      const stop = b.stops.find((s) => s.id === cancelStopId);
      if (stop) return stop;
    }
    return null;
  }, [dispatch, cancelStopId]);

  // Everything held back from the next commit, for the orange defer alert.
  // Defer is stop-scoped: held stops (with all their items) live in the
  // dispatch's rider-less DEFERRED batch(es) — there's no per-item defer flag.
  const deferredInfo = useMemo(() => {
    const batches: Array<{
      code: string;
      riderName: string | null;
      itemCount: number;
      deferReason: string | null;
      waybills: string[];
    }> = [];
    for (const b of dispatch?.batches ?? []) {
      if (b.status !== 'DEFERRED') continue;
      // Distinct waybills so ops can find the goods; shipments often split
      // into several items on one AWB.
      const waybills = Array.from(
        new Set(b.stops.flatMap((s) => s.items.map((it) => it.waybill)))
      );
      batches.push({
        code: b.code,
        riderName: b.assignedRiderName,
        itemCount: b.itemCount,
        deferReason: b.deferReason ?? null,
        waybills,
      });
    }
    return { batches };
  }, [dispatch]);

  // Raw DEFERRED batches (with stops) — a stable reference so the map/section
  // don't rebuild on every render. Feeds both the "Ditahan" section and the
  // map's ghost markers.
  const deferredBatches = useMemo(
    () => (dispatch?.batches ?? []).filter((b) => b.status === 'DEFERRED'),
    [dispatch]
  );

  // PLANNED batches — the valid targets for moving a stop between trips.
  const plannedBatches = useMemo(
    () => (dispatch?.batches ?? []).filter((b) => b.status === 'PLANNED'),
    [dispatch]
  );

  // Batches whose DS bridge errored at commit. Nothing shipped for these: the
  // parcels are still HUB_SCANNED at the hub, so on a PARTIAL/FAILED run this
  // is the headline exception ops has to act on.
  const failedBatches = useMemo(
    () => (dispatch?.batches ?? []).filter((b) => b.status === 'FAILED'),
    [dispatch]
  );

  // Flattened failure summary for the alert card — one row per failed trip,
  // with distinct `waybill (bookingId)` refs so ops can find the goods still at
  // the hub and match them against the client's order. A waybill can carry
  // several items, hence the de-dup; bookingId is dropped from the label when
  // the payload doesn't carry it.
  const failedInfo = useMemo(() => {
    const rows = failedBatches.map((b) => {
      const refs = new Map<string, string>();
      for (const stop of b.stops) {
        for (const item of stop.items) {
          const key = `${item.waybill}|${item.bookingId ?? ''}`;
          if (refs.has(key)) continue;
          refs.set(
            key,
            item.bookingId
              ? `${item.waybill} (${item.bookingId})`
              : item.waybill
          );
        }
      }
      return {
        id: b.id,
        code: b.code,
        riderName: b.assignedRiderName,
        riderCode: b.assignedRiderCode,
        tripSequence: b.tripSequence,
        itemCount: b.itemCount,
        stopCount: b.stops.length,
        waybills: Array.from(refs.values()),
      };
    });
    return {
      rows,
      itemCount: rows.reduce((n, r) => n + r.itemCount, 0),
      stopCount: rows.reduce((n, r) => n + r.stopCount, 0),
    };
  }, [failedBatches]);

  // The persisted header counters (totalBatches/totalItems/deferred*) can go
  // stale on the BE after a refresh/replan — the batches tree is the source
  // of truth, so every count we show is derived from it.
  const derivedCounts = useMemo(() => {
    const batches = dispatch?.batches ?? [];
    // "Active" = what actually goes out this run — neither held back
    // (DEFERRED) nor stranded at the hub by a bridge error (FAILED). Counting
    // FAILED as active made the header claim items that never left.
    const active = batches.filter(
      (b) => b.status !== 'DEFERRED' && b.status !== 'FAILED'
    );
    return {
      activeBatches: active.length,
      activeItems: active.reduce((n, b) => n + b.itemCount, 0),
      totalItems: batches.reduce((n, b) => n + b.itemCount, 0),
      // Stops where the rider collects goods (retrieval) instead of dropping.
      activePickupStops: active.reduce(
        (n, b) => n + b.stops.filter((s) => s.kind === 'PICKUP').length,
        0
      ),
    };
  }, [dispatch]);

  // Trip kinds among the shipping batches — feeds the Batches stat's
  // "N dropoff · M pickup" sub-note (a batch is never both).
  const batchKindCounts = useMemo(() => {
    const active = (dispatch?.batches ?? []).filter(
      (b) => b.status !== 'DEFERRED' && b.status !== 'FAILED'
    );
    let pickup = 0;
    let drop = 0;
    for (const b of active) {
      const kind = tripKindOf(b.stops);
      if (kind === 'PICKUP') pickup += 1;
      else if (kind === 'DROPOFF') drop += 1;
    }
    return { pickup, drop };
  }, [dispatch]);

  // What delivery-service said when a bridge failed. `errorDetail` is one
  // line per failed call — "batch <id> (provider <n>): <message>" — plus a
  // "stage:" header; lines naming no batch land in `general`. Empty until
  // a commit has actually failed, so this costs nothing on a PREVIEW.
  const dsReasons = useMemo(() => {
    const byBatch = new Map<string, string[]>();
    const general: string[] = [];
    for (const raw of (dispatch?.errorDetail ?? '').split('\n')) {
      const line = raw.trim();
      if (!line || line.startsWith('stage:')) continue;
      const m = /^batch ([0-9a-f-]{36}) \(provider [^)]*\): (.*)$/i.exec(line);
      if (m) {
        const list = byBatch.get(m[1]) ?? [];
        list.push(m[2]);
        byBatch.set(m[1], list);
      } else {
        general.push(line);
      }
    }
    return { byBatch, general };
  }, [dispatch]);

  /**
   * Load the dispatch. `silent` re-reads it WITHOUT the full-page loading
   * skeleton and without a toast of its own — for refreshes that back an
   * action whose outcome the caller already reports (see submitCommit), where
   * blanking the page and stacking a second toast would only obscure it.
   */
  const fetchDispatch = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}) => {
      if (!id) return;
      if (!silent) setLoading(true);
      try {
        const data = await dispatchesAPI.get(id);
        setDispatch(data);
      } catch (err) {
        if (!silent) {
          showErrorToast(
            'Could not load dispatch',
            err instanceof Error ? err.message : 'Unknown error'
          );
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [id]
  );

  useEffect(() => {
    fetchDispatch();
  }, [fetchDispatch]);

  // Download this dispatch as CSV — one row per item, incl. Delivery UID
  // (empty until the DS bridge for that batch succeeds).
  const handleExportCsv = useCallback(async () => {
    if (!id) return;
    setExporting(true);
    try {
      await dispatchesAPI.export(id, dispatch?.code);
    } catch (err) {
      showErrorToast(
        'Download CSV gagal',
        err instanceof Error ? err.message : 'Unknown error'
      );
    } finally {
      setExporting(false);
    }
  }, [id, dispatch?.code]);

  // Origin fetched once, used both for the header label and for the map's
  // pin + bounds. Fetched BY ID, not by scanning the hub list: that list is
  // `type=HUB&hasStaff=true`, so a scheduled dispatch departing from a client
  // pitstop found nothing and the map lost its origin pin (QA ISSUE-008).
  useEffect(() => {
    if (!dispatch?.hubId) return;
    let cancelled = false;
    hubsAPI
      .getById(Number(dispatch.hubId))
      .then((origin: Hub) => {
        if (!cancelled) setHub(origin);
      })
      .catch(() => {
        if (!cancelled) setHub(null);
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch?.hubId]);

  // Group map-visible batches (PLANNED + DISPATCHED + FAILED) by rider, sort
  // each rider's trips by sequence ASC. FAILED trips are listed alongside the
  // shipping ones (so the exception is visible where ops looks for the rider's
  // work) but excluded from the load roll-ups — they carry nothing.
  const plannedByRider = useMemo(() => {
    if (!dispatch) return [] as Array<{
      rider: DispatchRiderSnapshot;
      batches: DispatchDetailBatch[];
      shippingBatchCount: number;
      failedBatchCount: number;
      totalItems: number;
      totalWeightKg: number;
      assignedTimeMinutes: number;
      unscannedItemCount: number;
    }>;
    const groups = new Map<
      number,
      {
        rider: DispatchRiderSnapshot;
        batches: DispatchDetailBatch[];
        shippingBatchCount: number;
        failedBatchCount: number;
        totalItems: number;
        totalWeightKg: number;
        assignedTimeMinutes: number;
        unscannedItemCount: number;
      }
    >();
    for (const batch of dispatch.batches) {
      if (
        !MAP_VISIBLE_BATCH_STATUSES.has(batch.status) ||
        batch.assignedRiderId == null
      ) {
        continue;
      }
      const riderId = batch.assignedRiderId;
      const rider =
        dispatch.ridersSnapshot.find((r) => r.id === riderId) ?? {
          id: riderId,
          code: batch.assignedRiderCode ?? '',
          name: batch.assignedRiderName ?? '',
        };
      const existing = groups.get(riderId) ?? {
        rider,
        batches: [],
        shippingBatchCount: 0,
        failedBatchCount: 0,
        totalItems: 0,
        totalWeightKg: 0,
        assignedTimeMinutes: 0,
        unscannedItemCount: 0,
      };
      existing.batches.push(batch);
      if (batch.status === 'FAILED') {
        existing.failedBatchCount += 1;
        groups.set(riderId, existing);
        continue;
      }
      existing.shippingBatchCount += 1;
      existing.totalItems += batch.itemCount;
      existing.totalWeightKg += batch.totalWeightKg;
      existing.assignedTimeMinutes += batch.estimatedTimeMinutes;
      // Active (shipping) unscanned only — a rider's ⚠ shouldn't count deferred
      // items that aren't going out in this dispatch.
      existing.unscannedItemCount += batch.activeUnscannedItemCount ?? 0;
      groups.set(riderId, existing);
    }
    const list = Array.from(groups.values());
    for (const g of list) {
      g.batches.sort(
        (a: DispatchDetailBatch, b: DispatchDetailBatch) =>
          (a.tripSequence ?? 0) - (b.tripSequence ?? 0)
      );
    }
    // Stable group order: id ASC (matches backend LPT tie-break).
    return list.sort((a, b) => a.rider.id - b.rider.id);
  }, [dispatch]);

  // Flat list of PLANNED batches in left-rail display order, plus an
  // id → index lookup. The index drives the per-batch color so the chip on
  // a BatchCard always matches its route on the map.
  const { plannedBatchesFlat, batchIndexById } = useMemo(() => {
    const flat: DispatchDetailBatch[] = [];
    const idx = new Map<string, number>();
    for (const group of plannedByRider) {
      for (const batch of group.batches) {
        idx.set(batch.id, flat.length);
        flat.push(batch);
      }
    }
    return { plannedBatchesFlat: flat, batchIndexById: idx };
  }, [plannedByRider]);

  // Batches the map should currently draw. Two filters compose:
  //   1. Solo rider — when a rider is "soloed" (clicking their header card),
  //      only that rider's batches are visible. Overrides hiddenRiderIds.
  //   2. Hidden riders (eye-button) — explicit per-rider hide; respected
  //      only when no rider is soloed.
  const visiblePlannedBatches = useMemo(
    () =>
      plannedBatchesFlat.filter((b) => {
        if (b.assignedRiderId == null) return true;
        if (soloRiderId != null) return b.assignedRiderId === soloRiderId;
        return !hiddenRiderIds.has(b.assignedRiderId);
      }),
    [plannedBatchesFlat, hiddenRiderIds, soloRiderId]
  );

  // Trips that are actually going out — the sidebar/map list minus the failed
  // ones. Drives every "what ships this run" number in the stats strip.
  const shippingBatchesFlat = useMemo(
    () => plannedBatchesFlat.filter((b) => b.status !== 'FAILED'),
    [plannedBatchesFlat]
  );

  // Stats strip totals. Pulled from dispatch + planned aggregates so
  // "stops" and "demand" reflect what the planner committed to. Capacity
  // and utilization are derived from the dispatch's shift budget.
  const stats = useMemo(() => {
    const totalStops = shippingBatchesFlat.reduce(
      (n, b) => n + b.stopCount,
      0
    );
    const demandMinutes = shippingBatchesFlat.reduce(
      (n, b) => n + b.estimatedTimeMinutes,
      0
    );
    const capacityMinutes =
      (dispatch?.ridersSnapshot.length ?? 0) * (dispatch?.shiftMinutes ?? 0);
    const utilization =
      capacityMinutes > 0
        ? Math.round((demandMinutes / capacityMinutes) * 100)
        : 0;
    return {
      // Derived from the batches tree — dispatch.totalItems can be stale.
      items: derivedCounts.activeItems,
      stops: totalStops,
      batches: shippingBatchesFlat.length,
      demandMinutes,
      capacityMinutes,
      utilization,
    };
  }, [dispatch, shippingBatchesFlat, derivedCounts]);

  // Clear selection if the user hides the rider whose batch is selected.
  // Avoids a "highlighted card on the sidebar but no route on the map"
  // discontinuity.
  useEffect(() => {
    if (!selectedBatchId) return;
    // A held (DEFERRED) batch is a valid selection too — it's drawn on the map
    // as ghost markers, so don't clear it just because it isn't a routed batch.
    const stillVisible =
      visiblePlannedBatches.some((b) => b.id === selectedBatchId) ||
      deferredBatches.some((b) => b.id === selectedBatchId);
    if (!stillVisible) setSelectedBatchId(null);
  }, [selectedBatchId, visiblePlannedBatches, deferredBatches]);

  // Auto-expand the rider whose batch was just selected. Critical for the
  // two-way scroll below: if the selection came from a map marker click,
  // the BatchCard needs to be rendered before scrollIntoView can find its
  // DOM node. This effect fires first, the scroll effect fires after the
  // re-render (gated on expandedRiderIds in its deps).
  useEffect(() => {
    if (!selectedBatchId) return;
    const batch = plannedBatchesFlat.find((b) => b.id === selectedBatchId);
    const riderId = batch?.assignedRiderId;
    if (riderId == null) return;
    setExpandedRiderIds((prev) => {
      if (prev.has(riderId)) return prev;
      const next = new Set(prev);
      next.add(riderId);
      return next;
    });
  }, [selectedBatchId, plannedBatchesFlat]);

  // When selection comes from the map (marker click) the sidebar might be
  // scrolled away. RAF gives the BatchCard one frame to mount (in case the
  // owning rider was just expanded above) before we try to find its ref.
  // scrollIntoView is a no-op when the element is already in view, so this
  // doesn't bounce on sidebar-originated clicks.
  useEffect(() => {
    if (!selectedBatchId) return;
    const raf = requestAnimationFrame(() => {
      const node = batchRefsMap.current.get(selectedBatchId);
      node?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
    return () => cancelAnimationFrame(raf);
  }, [selectedBatchId, expandedRiderIds]);

  const toggleRiderVisibility = useCallback((riderId: number) => {
    setHiddenRiderIds((prev) => {
      const next = new Set(prev);
      if (next.has(riderId)) next.delete(riderId);
      else next.add(riderId);
      return next;
    });
  }, []);

  const toggleRiderExpanded = useCallback((riderId: number) => {
    // Header click both opens the accordion and solos the rider on the map.
    // Same rider clicked again → close accordion + clear solo. Different
    // rider clicked → solo the new rider; collapse all others so the
    // sidebar stays focused on the one ops is inspecting.
    setExpandedRiderIds((prev) => {
      const wasExpanded = prev.has(riderId);
      const next = new Set<number>();
      if (!wasExpanded) next.add(riderId);
      return next;
    });
    setSoloRiderId((curr) => (curr === riderId ? null : riderId));
  }, []);

  // Ridersnapshots not yet showing any PLANNED batches (idle on this dispatch).
  const idleRiders = useMemo(() => {
    if (!dispatch) return [];
    const busyIds = new Set(plannedByRider.map((g) => g.rider.id));
    return dispatch.ridersSnapshot.filter((r) => !busyIds.has(r.id));
  }, [dispatch, plannedByRider]);

  const isEditable = dispatch?.status === 'PREVIEW';

  // ─── PATCH handlers ───────────────────────────────────────────────────
  const applyStopReassign = useCallback(
    async (stopId: string, rider: DispatchRiderSnapshot) => {
      if (!dispatch || !id) return;
      setBusy(stopId);
      try {
        const updated = await dispatchesAPI.update(id, {
          stopReassignments: [{ stopId, rider }],
        });
        setDispatch(updated);
        showSuccessToast(
          'Stop reassigned',
          `Moved to ${rider.name}`
        );
      } catch (err) {
        showErrorToast(
          'Could not reassign stop',
          err instanceof Error ? err.message : 'Unknown error'
        );
      } finally {
        setBusy(null);
        setReassignStopId(null);
      }
    },
    [dispatch, id]
  );

  // Bulk stop reassignment — used by the "Ditahan" section to assign a whole
  // DEFERRED batch (all its stops) or a single held stop onto a rider. Reuses
  // the same PATCH stopReassignments lever as applyStopReassign. `busyKey`
  // drives which button spins (batch id for assign-all, stop id per-row).
  const applyStopsReassign = useCallback(
    async (
      stopIds: string[],
      rider: DispatchRiderSnapshot,
      busyKey: string
    ) => {
      if (!dispatch || !id || stopIds.length === 0) return;
      setBusy(busyKey);
      try {
        const updated = await dispatchesAPI.update(id, {
          stopReassignments: stopIds.map((stopId) => ({ stopId, rider })),
        });
        setDispatch(updated);
        showSuccessToast(
          stopIds.length > 1 ? 'Batch di-assign' : 'Stop di-assign',
          `Dipindah ke ${rider.name}`
        );
      } catch (err) {
        showErrorToast(
          'Gagal assign ke rider',
          err instanceof Error ? err.message : 'Unknown error'
        );
      } finally {
        setBusy(null);
      }
    },
    [dispatch, id]
  );

  // Move one stop into a specific PLANNED batch/trip (§move-stop-between-
  // batches). Sends `toBatchId`; `rider` is ignored by the BE but must be a
  // valid ridersSnapshot entry, so we pass the target batch's own rider.
  const applyStopMoveToBatch = useCallback(
    async (stopId: string, toBatch: DispatchDetailBatch) => {
      if (!dispatch || !id || toBatch.assignedRiderId == null) return;
      setBusy(stopId);
      try {
        const updated = await dispatchesAPI.update(id, {
          stopReassignments: [
            {
              stopId,
              toBatchId: toBatch.id,
              rider: {
                id: toBatch.assignedRiderId,
                code: toBatch.assignedRiderCode ?? '',
                name: toBatch.assignedRiderName ?? '',
              },
            },
          ],
        });
        setDispatch(updated);
        showSuccessToast(
          'Stop dipindah',
          `Ke ${
            toBatch.tripSequence != null
              ? `Trip #${toBatch.tripSequence}`
              : toBatch.code
          }`
        );
      } catch (err) {
        showErrorToast(
          'Gagal memindah stop',
          err instanceof Error ? err.message : 'Unknown error'
        );
      } finally {
        setBusy(null);
        setMoveStopTarget(null);
      }
    },
    [dispatch, id]
  );

  // ─── drag-to-reorder (stops within a trip, trips within a rider) ────────
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Persist a manual stop order for one batch. Optimistic: the local tree is
  // re-ordered immediately so the drop doesn't snap back while the request
  // runs; the response (fresh legs + re-snapshotted route) then replaces it,
  // and an error restores the pre-drag detail.
  const applyStopReorder = useCallback(
    async (batch: DispatchDetailBatch, orderedStopIds: string[]) => {
      if (!dispatch || !id) return;
      const prev = dispatch;
      const stopsById = new Map(batch.stops.map((s) => [s.id, s]));
      setDispatch({
        ...dispatch,
        batches: dispatch.batches.map((b) =>
          b.id === batch.id
            ? {
                ...b,
                stops: orderedStopIds.flatMap((sid, i) => {
                  const stop = stopsById.get(sid);
                  return stop ? [{ ...stop, sequence: i + 1 }] : [];
                }),
              }
            : b
        ),
      });
      setBusy(batch.id);
      try {
        const updated = await dispatchesAPI.reorderStops(
          id,
          batch.id,
          orderedStopIds
        );
        setDispatch(updated);
        showSuccessToast(
          'Urutan stop diubah',
          `${batch.code} — rute & estimasi dihitung ulang`
        );
      } catch (err) {
        setDispatch(prev);
        showErrorToast(
          'Gagal mengubah urutan stop',
          err instanceof Error ? err.message : 'Unknown error'
        );
      } finally {
        setBusy(null);
      }
    },
    [dispatch, id]
  );

  // Persist a manual trip order for one rider. Trip reorder moves no stops,
  // so only tripSequence changes — optimistic update + revert on error.
  const applyTripReorder = useCallback(
    async (rider: DispatchRiderSnapshot, orderedBatchIds: string[]) => {
      if (!dispatch || !id) return;
      const prev = dispatch;
      const seqById = new Map(orderedBatchIds.map((bid, i) => [bid, i + 1]));
      setDispatch({
        ...dispatch,
        batches: dispatch.batches.map((b) =>
          seqById.has(b.id)
            ? { ...b, tripSequence: seqById.get(b.id) ?? b.tripSequence }
            : b
        ),
      });
      setBusy(`trip-order-${rider.id}`);
      try {
        const updated = await dispatchesAPI.reorderTrips(
          id,
          rider.id,
          orderedBatchIds
        );
        setDispatch(updated);
        showSuccessToast('Urutan trip diubah', `Trip ${rider.name} diurut ulang`);
      } catch (err) {
        setDispatch(prev);
        showErrorToast(
          'Gagal mengubah urutan trip',
          err instanceof Error ? err.message : 'Unknown error'
        );
      } finally {
        setBusy(null);
      }
    },
    [dispatch, id]
  );

  // Merge one trip into another of the same rider — move ALL of the source
  // batch's stops into the target batch in one PATCH. The emptied source batch
  // is deleted and the target re-routed by the BE. Same lever as move-stop.
  const applyMergeTrips = useCallback(
    async (
      sourceBatch: DispatchDetailBatch,
      targetBatch: DispatchDetailBatch
    ) => {
      if (!dispatch || !id || targetBatch.assignedRiderId == null) return;
      const stopIds = sourceBatch.stops.map((s) => s.id);
      if (stopIds.length === 0) return;
      setBusy(sourceBatch.id);
      try {
        const rider: DispatchRiderSnapshot = {
          id: targetBatch.assignedRiderId,
          code: targetBatch.assignedRiderCode ?? '',
          name: targetBatch.assignedRiderName ?? '',
        };
        const updated = await dispatchesAPI.update(id, {
          stopReassignments: stopIds.map((stopId) => ({
            stopId,
            toBatchId: targetBatch.id,
            rider,
          })),
        });
        setDispatch(updated);
        showSuccessToast(
          'Trip digabung',
          `Ke ${
            targetBatch.tripSequence != null
              ? `Trip #${targetBatch.tripSequence}`
              : targetBatch.code
          }`
        );
      } catch (err) {
        showErrorToast(
          'Gagal menggabung trip',
          err instanceof Error ? err.message : 'Unknown error'
        );
      } finally {
        setBusy(null);
        setMergeTripSource(null);
      }
    },
    [dispatch, id]
  );

  const applyRiderReassign = useCallback(
    async (
      fromRiderId: number,
      toRiderId: number,
      mode: RiderReassignMode
    ) => {
      if (!dispatch || !id) return;
      try {
        const updated = await dispatchesAPI.reassignRider(id, {
          fromRiderId,
          toRiderId,
          mode,
        });
        setDispatch(updated);
        const toName =
          updated.ridersSnapshot.find((r) => r.id === toRiderId)?.name ??
          'rider';
        showSuccessToast(
          mode === 'SWAP' ? 'Batches swapped' : 'Batches reassigned',
          mode === 'SWAP'
            ? `Exchanged batches with ${toName}`
            : `Moved all batches to ${toName}`
        );
      } catch (err) {
        showErrorToast(
          mode === 'SWAP'
            ? 'Could not swap batches'
            : 'Could not reassign batches',
          err instanceof Error ? err.message : 'Unknown error'
        );
      } finally {
        setReassignRiderSource(null);
      }
    },
    [dispatch, id]
  );

  const applyBatchReassign = useCallback(
    async (
      batchId: string,
      toRiderId: number,
      mode: BatchReassignMode
    ) => {
      if (!dispatch || !id) return;
      setBusy(batchId);
      try {
        const updated = await dispatchesAPI.reassignBatch(id, batchId, {
          toRiderId,
          mode,
        });
        setDispatch(updated);
        const toName =
          updated.ridersSnapshot.find((r) => r.id === toRiderId)?.name ??
          'rider';
        showSuccessToast(
          mode === 'SWAP' ? 'Batch swapped' : 'Batch reassigned',
          mode === 'SWAP'
            ? `Exchanged batches with ${toName}`
            : `Moved batch to ${toName}`
        );
      } catch (err) {
        showErrorToast(
          mode === 'SWAP'
            ? 'Could not swap batch'
            : 'Could not reassign batch',
          err instanceof Error ? err.message : 'Unknown error'
        );
      } finally {
        setBusy(null);
        setReassignBatchTarget(null);
      }
    },
    [dispatch, id]
  );

  // Remove a stop's items (§D4) — optionally marking them hilang. This is
  // the escape hatch for the commit gate: a CANCELLED item no longer counts
  // as unscanned, so its shipment passes.
  const applyStopDisposition = useCallback(
    async (
      stopId: string,
      disposition: DispatchDisposition,
      reason: string
    ) => {
      if (!dispatch || !id) return;
      setBusy(stopId);
      try {
        const updated = await dispatchesAPI.disposeStop(
          id,
          stopId,
          disposition,
          reason || undefined
        );
        setDispatch(updated);
        // The item set changed — a stale gate rejection is no longer accurate.
        setUnscannedError(null);
        showSuccessToast(
          disposition === 'CANCEL'
            ? 'Stop dibatalkan'
            : disposition === 'LOST'
            ? 'Stop ditandai hilang'
            : 'Stop ditahan',
          disposition === 'DEFER'
            ? 'Stop dipindah ke Ditahan — assign ke rider untuk kembalikan'
            : 'Item dikeluarkan dari dispatch'
        );
      } catch (err) {
        showErrorToast(
          'Gagal memproses stop',
          err instanceof Error ? err.message : 'Unknown error'
        );
      } finally {
        setBusy(null);
        setCancelStopId(null);
      }
    },
    [dispatch, id]
  );

  // Item disposition — terminal only (CANCEL/LOST → item endpoint, keyed by
  // items.id = item.itemId). Item-level defer no longer exists; defer is
  // stop-scoped (see applyStopDisposition).
  const applyItemDisposition = useCallback(
    async (
      item: DispatchDetailItem,
      disposition: 'CANCEL' | 'LOST',
      reason: string
    ) => {
      if (!dispatch || !id) return;
      setBusy(item.id);
      try {
        const updated = await dispatchesAPI.disposeItems(
          id,
          disposition,
          [item.itemId],
          reason || undefined
        );
        setDispatch(updated);
        setUnscannedError(null);
        showSuccessToast(
          disposition === 'CANCEL' ? 'Item dibatalkan' : 'Item ditandai hilang',
          `${item.waybill} keluar dari rencana`
        );
      } catch (err) {
        showErrorToast(
          'Gagal memproses item',
          err instanceof Error ? err.message : 'Unknown error'
        );
      } finally {
        setBusy(null);
        setCancelItemTarget(null);
      }
    },
    [dispatch, id]
  );

  // Widen the frozen ridersSnapshot (§D5). No re-plan — ops moves work to
  // the new riders via the existing reassign actions.
  const applyAddRiders = useCallback(
    async (riders: DispatchRiderSnapshot[]) => {
      if (!dispatch || !id) return;
      try {
        const updated = await dispatchesAPI.addRiders(id, { riders });
        setDispatch(updated);
        showSuccessToast(
          `Rider${riders.length === 1 ? '' : 's'} added`,
          `${riders.map((r) => r.name).join(', ')} — reassign batches or stops to put them to work`
        );
        setAddRidersOpen(false);
      } catch (err) {
        showErrorToast(
          'Could not add riders',
          err instanceof Error ? err.message : 'Unknown error'
        );
      }
    },
    [dispatch, id]
  );

  // Force-assign oversized items (over the run's caps) to a rider — the BE
  // creates a dedicated `forced` batch with the capacity caps bypassed.
  const applyForceAssign = useCallback(
    async (itemIds: string[], rider: DispatchRiderSnapshot) => {
      if (!dispatch || !id) return;
      try {
        const updated = await dispatchesAPI.forceAssign(id, {
          itemIds,
          rider,
        });
        setDispatch(updated);
        showSuccessToast(
          'Force assigned',
          `${itemIds.length} item oversized → ${rider.name} (batch FORCED, cap dilewati)`
        );
        setForceAssignOpen(false);
      } catch (err) {
        showErrorToast(
          'Could not force assign',
          err instanceof Error ? err.message : 'Unknown error'
        );
      }
    },
    [dispatch, id]
  );

  // Pull post-preview hub arrivals into the plan (§D12).
  const applyRefreshPlan = useCallback(
    async (strategy: RefreshDispatchStrategy) => {
      if (!dispatch || !id) return;
      setRefreshingPlan(true);
      try {
        const updated = await dispatchesAPI.refresh(id, { strategy });
        setDispatch(updated);
        setUnscannedError(null);
        setRefreshPlanOpen(false);
        const summary = updated.refreshSummary;
        showSuccessToast(
          'Barang baru ditambahkan',
          summary
            ? `${summary.addedItems} item masuk dalam ${summary.addedBatches} batch baru`
            : 'Rencana diperbarui'
        );
      } catch (err) {
        showErrorToast(
          'Gagal menarik barang baru',
          err instanceof Error ? err.message : 'Unknown error'
        );
      } finally {
        setRefreshingPlan(false);
      }
    },
    [dispatch, id]
  );

  const submitCommit = useCallback(async () => {
    if (!dispatch || !id) return;
    setCommitting(true);
    try {
      const updated = await dispatchesAPI.commit(id);
      setDispatch(updated);
      setCommitOpen(false);
      setUnscannedError(null);
      const superseded = updated.supersededDispatchCodes ?? [];
      showSuccessToast(
        'Dispatch committed',
        `${updated.code} → ${updated.status}` +
          (superseded.length > 0
            ? ` · superseded ${superseded.join(', ')}`
            : '')
      );
    } catch (err) {
      // A rejected commit does NOT mean nothing happened. The DS bridge
      // applies per batch and only then throws: BRIDGE_FAILED has already
      // flipped the failed batches to FAILED and reverted their items to
      // HUB_SCANNED, so the dispatch on screen is stale the moment the error
      // lands. After a network/timeout failure the outcome is simply unknown.
      // Re-read before reporting — without this the page kept rendering a
      // PREVIEW that no longer existed and ops had to reload by hand.
      await fetchDispatch({ silent: true });
      if (err instanceof UnscannedItemsError) {
        // Keep the modal open and show the gate's item list (§D10) — ops
        // resolves items (scan / mark hilang) and re-commits from here.
        setUnscannedError(err);
      } else {
        // Anything else is settled server-side; leaving the commit modal open
        // would invite a second attempt against state that already moved.
        setCommitOpen(false);
        showErrorToast(
          'Could not commit dispatch',
          err instanceof Error ? err.message : 'Unknown error'
        );
      }
    } finally {
      setCommitting(false);
    }
  }, [dispatch, id, fetchDispatch]);

  // ─── render ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex h-full items-center justify-center p-12 text-sm text-text-sub-600">
        Loading dispatch…
      </div>
    );
  }
  if (!dispatch) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-12 text-sm text-text-sub-600">
        <p>Dispatch not found.</p>
        <Button tone="neutral" style="stroke" onClick={() => navigate('/dispatches')}>
          Back to dispatches
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-3 p-6">
      {/* ── Header ───────────────────────────────────────────────────── */}
      {/* Three quiet rows: crumb + provenance, identity + actions, context
          chips. Every COUNT lives in the stats strip below, never here, and
          every exception has one fixed home (a strip cell or an alert card).
          The one exception allowed up on the title row is a failed DS bridge:
          it means goods that never left after a commit. Layout + rationale:
          docs/dispatch/dispatch-header-mockup-v1.html. */}
      <div className="flex shrink-0 flex-col gap-2.5 border-b border-stroke-soft-200 pb-3">
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate('/dispatches')}
            className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.18em] text-text-sub-600 transition-colors hover:text-accent"
          >
            <i className="ri-arrow-left-line text-[14px]" />
            Dispatches
          </button>
          <span className="truncate text-[11px] text-text-soft-400">
            Dibuat {formatDateTime(dispatch.createdAt)} · oleh{' '}
            <span className="font-semibold text-text-sub-600">
              {dispatch.triggeredBy}
            </span>
            {dispatch.committedAt && (
              <>
                {' '}
                · Committed{' '}
                <span className="font-semibold text-text-sub-600">
                  {formatDateTime(dispatch.committedAt)}
                </span>
              </>
            )}
          </span>
        </div>

        <div className="flex flex-col gap-2.5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5">
            <h1 className="font-mono text-[22px] font-extrabold tracking-tight text-text-strong-950 leading-none">
              {dispatch.code}
            </h1>
            <DispatchStatusBadge status={dispatch.status} />
            {/* Auto = created by the scheduled pitstop tick, not an ops user. */}
            <AutoDispatchBadge triggeredBy={dispatch.triggeredBy} />
            {failedInfo.rows.length > 0 && (
              <button
                type="button"
                onClick={() =>
                  failedCardRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                  })
                }
                className="inline-flex"
                title="Lihat trip yang gagal"
              >
                <Badge
                  status="error"
                  type="left-icon"
                  icon={<i className="ri-error-warning-line" aria-hidden="true" />}
                >
                  Bridge gagal · {failedInfo.rows.length} trip
                </Badge>
              </button>
            )}
          </div>

          {/* Actions, by weight: one black Commit, the frequent "Tarik barang
              baru", one amber exception button that only exists while there
              is an exception, and everything else behind ⋯. On narrow
              screens Commit jumps to the front of its own row. After commit
              the bar collapses to Download CSV + ⋯. */}
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {isEditable ? (
              <>
                {dispatch.unbatchableCount > 0 && (
                  <Button
                    tone="neutral"
                    style="stroke"
                    className="border-(--dash-orange-300) text-(--state-warning-dark) hover:bg-(--state-warning-lighter) hover:text-(--state-warning-dark)"
                    onClick={() => setForceAssignOpen(true)}
                  >
                    <i className="ri-scales-3-line mr-1.5 text-[14px]" />
                    {dispatch.unbatchableCount} unbatchable · Force assign
                  </Button>
                )}
                <Button
                  tone="neutral"
                  style="stroke"
                  onClick={() => setRefreshPlanOpen(true)}
                  disabled={refreshingPlan}
                >
                  <i className="ri-download-cloud-2-line mr-1.5 text-[14px]" />
                  {refreshingPlan ? 'Memproses…' : 'Tarik barang baru'}
                </Button>
                <Button
                  className="order-first lg:order-none"
                  onClick={() => setCommitOpen(true)}
                  disabled={dispatch.batches.length === 0}
                >
                  <i className="ri-check-double-line mr-1.5 text-[14px]" />
                  Commit dispatch
                </Button>
              </>
            ) : (
              // CSV export (TRD §4.9): shipment × rider × delivery-UID
              // manifest — the main thing ops pulls from a committed run, so
              // it earns a visible button here (it sits in ⋯ while PREVIEW).
              <Button
                tone="neutral"
                style="stroke"
                onClick={() => void handleExportCsv()}
                disabled={exporting}
              >
                <i
                  className={`mr-1.5 text-[14px] ${
                    exporting
                      ? 'ri-loader-4-line animate-spin'
                      : 'ri-download-2-line'
                  }`}
                />
                {exporting ? 'Menyiapkan…' : 'Download CSV'}
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  tone="neutral"
                  style="stroke"
                  className="px-2.5"
                  aria-label="Aksi lainnya"
                  title="Lainnya"
                >
                  <i className="ri-more-2-fill text-[16px]" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => void fetchDispatch()}>
                  <i className="ri-refresh-line" />
                  Refresh
                </DropdownMenuItem>
                {isEditable && (
                  <DropdownMenuItem
                    onSelect={() => void handleExportCsv()}
                    disabled={exporting}
                  >
                    <i className="ri-download-2-line" />
                    Download CSV
                  </DropdownMenuItem>
                )}
                {isEditable && (
                  <DropdownMenuItem onSelect={() => setAddRidersOpen(true)}>
                    <i className="ri-user-add-line" />
                    Add riders
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => setRunDetailsOpen(true)}>
                  <i className="ri-information-line" />
                  Run details
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Context chips: where, how, for whom, with how many. The hub chip
            shows code · name only when they differ (they often don't). */}
        <div className="flex flex-wrap items-center gap-1.5">
          {/* Name first — that's what ops calls the place; the code trails
              in mono. Snapshot frozen on the dispatch is the source (no extra
              lookup, survives a hub rename); the live pitstop fetch only fills
              rows from before the snapshot existed. */}
          <HeaderChip icon="ri-map-pin-2-line">
            {(() => {
              const name = dispatch.hub?.name ?? hub?.name ?? null;
              const code = dispatch.hub?.code ?? hub?.code ?? null;
              if (!name && !code) return `Hub ${dispatch.hubId}`;
              return (
                <>
                  <span className="text-text-strong-950">{name ?? code}</span>
                  {code && name && code !== name && (
                    <>
                      <span className="text-text-soft-400">·</span>
                      <span className="font-mono text-[10.5px]">{code}</span>
                    </>
                  )}
                </>
              );
            })()}
          </HeaderChip>
          {dispatch.mode === 'PRE_DISPATCH' && (
            <HeaderChip icon="ri-flashlight-line">Pre-dispatch</HeaderChip>
          )}
          {(dispatch.clients && dispatch.clients.length > 0
            ? dispatch.clients.map((c) => c.name)
            : ['Semua client']
          ).map((name) => (
            <HeaderChip key={name} icon="ri-store-2-line" tone="accent">
              {name}
            </HeaderChip>
          ))}
          <HeaderChip icon="ri-user-3-line">
            {dispatch.ridersSnapshot.length} rider
            {dispatch.ridersSnapshot.length === 1 ? '' : 's'}
          </HeaderChip>
        </div>
      </div>

      {/* Run details — planner caps + timestamps, one click from the ⋯ menu
          instead of a permanent header button. */}
      <FormModal
        open={runDetailsOpen}
        onClose={() => setRunDetailsOpen(false)}
        icon={<i className="ri-information-line" />}
        iconTone="primary"
        title="Run details"
        subtitle={`${dispatch.code} · caps & time model`}
        size="md"
        footer={
          <div className="flex w-full items-center justify-end">
            <Button
              tone="neutral"
              style="stroke"
              onClick={() => setRunDetailsOpen(false)}
            >
              Tutup
            </Button>
          </div>
        }
      >
        <div className="grid grid-cols-2 gap-x-5 gap-y-4">
          <DetailRow label="Created">
            {formatDateTime(dispatch.createdAt)}
          </DetailRow>
          <DetailRow label="Committed">
            {formatDateTime(dispatch.committedAt)}
          </DetailRow>
          <DetailRow label="Riders selected">
            {dispatch.ridersSnapshot.length}
          </DetailRow>
          <DetailRow label="Unbatchable items">
            {dispatch.unbatchableCount > 0 ? (
              <span className="font-semibold text-(--state-warning-dark)">
                {dispatch.unbatchableCount} oversized
              </span>
            ) : (
              <span className="text-text-sub-600">0</span>
            )}
          </DetailRow>
          <DetailRow label="Max weight">
            {fmtWeight(dispatch.maxWeightKg)}
          </DetailRow>
          <DetailRow label="Max volume">
            {fmtVolume(dispatch.maxVolumeM3)}
          </DetailRow>
          <DetailRow label="Max stop radius">
            {dispatch.maxStopRadiusKm.toFixed(2)} km
          </DetailRow>
          <DetailRow label="Shift budget">
            {fmtMinutes(dispatch.shiftMinutes)} per rider
          </DetailRow>
        </div>
      </FormModal>

      {/* ── Cancelled notice ─────────────────────────────────────────── */}
      {/* A PREVIEW that lost to another commit at the same hub (§D6) — or was
          cancelled by hand. Nothing is locked; the numbers below are history. */}
      {dispatch.status === 'CANCELLED' && (
        <div className="flex shrink-0 items-start gap-2.5 rounded-lg border border-stroke-soft-200 bg-bg-weak-50 px-4 py-3">
          <i
            className="ri-forbid-line mt-0.5 shrink-0 text-[16px] text-text-sub-600"
            aria-hidden="true"
          />
          <div className="min-w-0">
            <p className="text-[13px] font-bold tracking-tight text-text-strong-950">
              Dispatch ini dibatalkan
            </p>
            <p className="mt-0.5 text-[11px] leading-relaxed text-text-sub-600">
              Biasanya karena dispatch lain di hub yang sama di-commit lebih
              dulu — preview yang tersisa otomatis ditutup. Tidak ada barang
              yang terkunci; buat preview baru kalau masih diperlukan. Angka
              di bawah adalah kondisi saat rencana ini dibuat.
            </p>
          </div>
        </div>
      )}

      {/* ── Failed-batch alert (bridge error) ────────────────────────── */}
      {/* Top of the page on purpose: a FAILED batch means the DS bridge errored
          and NOTHING in that trip shipped, while the rest of the dispatch went
          out fine. Without this card the only hint is the PARTIAL badge in the
          header, so ops could easily miss goods stranded at the hub. Lists the
          trip, its rider and its waybills; per-stop detail lives on the batch
          card in the rider sidebar (badge "Failed"). */}
      {failedInfo.rows.length > 0 && (
        <div
          ref={failedCardRef}
          className="shrink-0 rounded-lg border border-red-300 bg-red-50 px-4 py-3"
        >
          <div className="flex items-start gap-2.5">
            <span className="grid h-7 w-7 flex-none place-items-center rounded-lg bg-red-100 text-red-600">
              <i
                className="ri-error-warning-line text-[16px]"
                aria-hidden="true"
              />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-bold tracking-tight text-red-900">
                {failedInfo.rows.length} batch gagal dispatch (
                {failedInfo.itemCount} item) — barang tidak terkirim
              </p>
              <p className="mt-0.5 text-[11px] leading-relaxed text-red-700">
                Bridge ke Delivery System gagal, jadi trip ini tidak pernah
                sampai ke rider. Barang masih ada di hub — masukkan lagi ke
                dispatch berikutnya.
              </p>
              <ul className="mt-2 flex flex-col gap-1.5">
                {failedInfo.rows.map((row) => {
                  const shown = row.waybills.slice(0, 10);
                  const rest = row.waybills.length - shown.length;
                  return (
                    <li
                      key={row.id}
                      className="rounded-lg border border-red-200 border-l-[3px] border-l-red-500 bg-white px-2.5 py-2"
                    >
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                        <span className="font-mono text-[12px] font-semibold text-red-700 tabular-nums">
                          {row.code}
                        </span>
                        {row.tripSequence != null && (
                          <span className="text-[11px] text-text-sub-600">
                            Trip #{row.tripSequence}
                          </span>
                        )}
                        <span className="text-[12px] font-semibold text-text-strong-950">
                          {row.riderName ?? 'Tanpa rider'}
                        </span>
                        {row.riderCode && (
                          <span className="font-mono text-[10px] text-text-sub-600 tabular-nums">
                            {row.riderCode}
                          </span>
                        )}
                        <span className="ml-auto flex-none text-[11px] font-semibold text-text-sub-600 tabular-nums">
                          {row.itemCount} item · {row.stopCount} stop
                        </span>
                      </div>
                      {shown.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {shown.map((wb) => (
                            <span
                              key={wb}
                              className="rounded-md border border-red-200 bg-red-50 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-red-800"
                            >
                              {wb}
                            </span>
                          ))}
                          {rest > 0 && (
                            <span className="px-1 py-0.5 text-[10px] font-semibold text-red-700 tabular-nums">
                              +{rest} waybill lain
                            </span>
                          )}
                        </div>
                      )}
                      {/* What DS answered for THIS trip — ops no longer
                          needs someone to read the server log. */}
                      {(dsReasons.byBatch.get(row.id) ?? []).length > 0 && (
                        <div className="mt-2 flex items-start gap-2 border-t border-dashed border-red-200 pt-2">
                          <span className="flex-none pt-px text-[10px] font-bold uppercase tracking-[0.18em] text-red-700">
                            Alasan DS
                          </span>
                          <span className="min-w-0 break-words font-mono text-[11px] leading-relaxed text-red-900">
                            {dsReasons.byBatch.get(row.id)!.join(' | ')}
                          </span>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
              {dsReasons.general.length > 0 && (
                <div className="mt-2 flex items-start gap-2 border-t border-dashed border-red-200 pt-2">
                  <span className="flex-none pt-px text-[10px] font-bold uppercase tracking-[0.18em] text-red-700">
                    Alasan DS
                  </span>
                  <span className="min-w-0 break-words font-mono text-[11px] leading-relaxed text-red-900">
                    {dsReasons.general.join(' | ')}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Deferred alert (compact) ─────────────────────────────────── */}
      {/* Just the headline — full detail (waybills, per-item actions, assign)
          lives in the <DeferredSection> "Deferred (Ditahan)" panel at the top
          of the rider sidebar. This keeps held goods visible above the fold. */}
      {deferredInfo.batches.length > 0 && (
        <div className="rounded-lg border border-orange-200 bg-orange-50 px-4 py-2.5">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <i
              className="ri-pause-circle-line text-[16px] text-orange-600"
              aria-hidden="true"
            />
            <p className="text-[12px] font-semibold text-orange-800">
              {`${deferredInfo.batches.length} batch ditahan (${deferredInfo.batches.reduce(
                (n, b) => n + b.itemCount,
                0
              )} item)`}{' '}
              — tidak ikut terkirim saat commit
            </p>
            <span className="text-[11px] text-orange-700/80">
              Detail &amp; assign ada di panel “Deferred (Ditahan)”.
            </span>
            {/* Add riders belongs here, beside the held goods it can
                unblock, rather than as a permanent header button. */}
            {isEditable && (
              <Button
                tone="neutral"
                style="stroke"
                className="ml-auto h-8 border-orange-300 px-2.5 text-[12px] text-orange-800 hover:bg-orange-100 hover:text-orange-900"
                onClick={() => setAddRidersOpen(true)}
              >
                <i className="ri-user-add-line mr-1.5 text-[14px]" />
                Add riders
              </Button>
            )}
          </div>
          {/* Idle riders surfaced here (not just Run summary) — these are the
              riders that can pick up the held batch(es) right now. */}
          {idleRiders.length > 0 && (
            <div className="mt-2 pl-6">
              <p className="text-[11px] font-semibold text-orange-800">
                Rider tidak kebagian Batch:
              </p>
              <ol className="mt-0.5 list-decimal pl-4 text-[11px] text-orange-700/90">
                {idleRiders.map((r) => (
                  <li key={r.id}>{r.name}</li>
                ))}
              </ol>
              <p className="mt-1 text-[10px] leading-relaxed text-orange-700/70">
                Rider tidak kebagian batch karena titik pengantaran yang jauh
                (melebihi kapasitas waktu yang sudah ditetapkan) atau kapasitas
                dan lebar barang terlalu besar.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── Idle-riders alert ────────────────────────────────────────── */}
      {/* Standalone (above the fold) only when there's no deferred alert —
          when goods ARE held, the deferred alert already lists idle riders.
          Surfaces over-provisioned riders that carry no batch yet. */}
      {idleRiders.length > 0 && deferredInfo.batches.length === 0 && (
          <div className="flex items-start gap-2 rounded-lg border border-stroke-soft-200 bg-bg-weak-50 px-4 py-2.5">
            <i
              className="ri-user-unfollow-line mt-0.5 shrink-0 text-[16px] text-text-sub-600"
              aria-hidden="true"
            />
            <div className="min-w-0">
              <p className="text-[12px] font-semibold text-text-strong-950">
                Rider tidak kebagian Batch:
              </p>
              <ol className="mt-0.5 list-decimal pl-4 text-[12px] text-text-sub-600">
                {idleRiders.map((r) => (
                  <li key={r.id}>{r.name}</li>
                ))}
              </ol>
              <p className="mt-1 text-[10px] leading-relaxed text-text-sub-600">
                Belum kebagian batch — pindahkan batch ke mereka lewat tombol
                Reassign kalau perlu.
              </p>
            </div>
          </div>
        )}

      {/* ── Unscanned-items banner (§D9) ─────────────────────────────── */}
      {/* Dispatch-level scan progress + commit gate. Only meaningful while the
          dispatch is still editable (PREVIEW) — the "commit akan ditolak" copy
          is pre-commit, so we hide it once committed even if leftover deferred
          items are still unscanned. Counts include deferred items on purpose:
          per §D10 deferring is NOT enough to pass the gate — items must be
          scanned or removed/marked hilang. */}
      {isEditable && (dispatch.activeUnscannedItemCount ?? 0) > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <i className="ri-alert-line text-[18px] text-amber-600" />
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-semibold text-amber-800">
              {dispatch.activeUnscannedItemCount} dari {derivedCounts.activeItems}{' '}
              item yang akan dikirim belum di-scan di hub
            </p>
            <p className="text-[12px] text-amber-700 leading-relaxed">
              Commit ditolak selama masih ada item yang akan dikirim belum
              di-scan. Scan barang yang sudah tiba lewat halaman Inbound, atau
              hapus barang yang hilang lewat tombol "Remove" pada stop.
              {(dispatch.deferredUnscannedItemCount ?? 0) > 0 && (
                <>
                  {' '}
                  <span className="text-amber-800/80">
                    ({dispatch.deferredUnscannedItemCount} item deferred juga
                    belum di-scan — tidak ikut terkirim di dispatch ini.)
                  </span>
                </>
              )}
            </p>
          </div>
        </div>
      )}

      {/* ── Stats strip ─────────────────────────────────────────────── */}
      {/* At-a-glance dispatch totals. Lives directly under the header so
          ops sees the run shape without scanning the rest of the page.
          Numbers are dispatch-wide truth, not "what's on screen" — rider
          filter never changes these values. */}
      <div className="flex shrink-0 flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-stroke-soft-200 bg-bg-weak-50 px-4 py-2">
        <StatCell label="Items" value={stats.items.toLocaleString()} />
        <StatCell
          label="Stops"
          value={
            <>
              {stats.stops.toLocaleString()}
              {derivedCounts.activePickupStops > 0 && (
                // Same tones + order as everywhere else: dropoff (blue) first,
                // then pickup (teal).
                <span className="ml-1.5 text-[11px] font-semibold text-text-sub-600">
                  <span className="text-(--state-information-base)">
                    · {Math.max(0, stats.stops - derivedCounts.activePickupStops)}{' '}
                    dropoff
                  </span>{' '}
                  <span className="text-verified-base">
                    · {derivedCounts.activePickupStops} pickup
                  </span>
                </span>
              )}
            </>
          }
        />
        <StatCell
          label="Batches"
          value={
            <>
              {stats.batches}
              {deferredInfo.batches.length > 0 &&
                ` (${deferredInfo.batches.length}D)`}
              {batchKindCounts.pickup > 0 && (
                // Same tones as the trip chips: dropoff blue, pickup teal.
                <span className="ml-1.5 text-[11px] font-semibold text-text-sub-600">
                  <span className="text-(--state-information-base)">
                    · {batchKindCounts.drop} dropoff
                  </span>{' '}
                  <span className="text-verified-base">
                    · {batchKindCounts.pickup} pickup
                  </span>
                </span>
              )}
            </>
          }
        />
        {deferredInfo.batches.length > 0 && (
          <StatCell
            label="Deferred"
            value={`${deferredInfo.batches.reduce(
              (n, b) => n + b.itemCount,
              0
            )} items`}
            tone="defer"
          />
        )}
        {failedInfo.rows.length > 0 && (
          <StatCell
            label="Gagal dispatch"
            value={`${failedInfo.stopCount} stop${
              failedInfo.stopCount === 1 ? '' : 's'
            } / ${failedInfo.itemCount} item${
              failedInfo.itemCount === 1 ? '' : 's'
            }`}
            tone="error"
          />
        )}
        <StatCell
          label="Demand · Capacity"
          value={`${stats.demandMinutes} / ${stats.capacityMinutes} min`}
        />
        <StatCell label="Utilization" value={`${stats.utilization}%`} />
        {dispatch.unbatchableCount > 0 && (
          <StatCell
            label="Unbatchable"
            value={`${dispatch.unbatchableCount} oversized`}
            tone="warn"
          />
        )}
        {(dispatch.activeUnscannedItemCount ?? 0) > 0 && (
          <StatCell
            label="Belum scan"
            value={`${dispatch.activeUnscannedItemCount} items`}
            tone="warn"
          />
        )}
      </div>

      {/* ── Primary view: full-bleed map with a floating sidebar ─────── */}
      {/* Map fills the whole block. Sidebar floats on top of the right
          edge so the map gets the full width underneath. fitPadding tells
          the planner-side fit logic to keep markers out from under the
          sidebar. Sidebar uses an accordion so 20 riders still fit on
          screen without scrolling. */}
      <div className="relative flex-1 min-h-[420px] overflow-hidden rounded-lg border border-stroke-soft-200 bg-white">
        {/* Map — absolute fills, behind the sidebar */}
        <div className="absolute inset-0">
          <DispatchMap
            batches={visiblePlannedBatches}
            deferredBatches={deferredBatches}
            batchIndexById={batchIndexById}
            // Live pitstop when the lookup worked (has phone), else the
            // snapshot frozen on the dispatch — the pin must not vanish just
            // because core is unreachable.
            hub={
              hub ??
              (dispatch.hub &&
              dispatch.hub.latitude != null &&
              dispatch.hub.longitude != null
                ? {
                    code: dispatch.hub.code ?? '',
                    name: dispatch.hub.name,
                    latitude: dispatch.hub.latitude,
                    longitude: dispatch.hub.longitude,
                    address: dispatch.hub.address ?? null,
                  }
                : null)
            }
            selectedBatchId={selectedBatchId}
            onSelectBatch={setSelectedBatchId}
            fitPadding={{ top: 60, right: 420, bottom: 60, left: 60 }}
          />
        </div>

        {/* Show all batches — top-center so it clears both the Google
            zoom controls (top-left) and the floating sidebar (top-right). */}
        {selectedBatchId && (
          <button
            type="button"
            onClick={() => setSelectedBatchId(null)}
            className="absolute left-1/2 top-4 z-10 -translate-x-1/2 rounded-lg border border-stroke-soft-200 bg-white/95 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-text-sub-600 shadow-sm backdrop-blur transition-colors hover:text-accent"
          >
            <i className="ri-close-line mr-1 text-[13px]" />
            Show all batches
          </button>
        )}

        {/* Legend for the gray ghost markers — only when held stops are drawn.
            Bottom-left, clear of the Google logo (bottom-left is the map's,
            but sits low; this pill sits just above it). */}
        {deferredBatches.length > 0 && (
          <div className="absolute bottom-4 left-4 z-10 inline-flex items-center gap-1.5 rounded-lg border border-stroke-soft-200 bg-white/95 px-2.5 py-1.5 text-[11px] font-semibold text-text-sub-600 shadow-sm backdrop-blur">
            <span
              className="inline-block h-3 w-3 rounded-full border-2 bg-white"
              style={{ borderColor: '#78716c' }}
              aria-hidden="true"
            />
            Stop deferred (ditahan) · belum ter-rute
          </div>
        )}

        {/* Empty-state overlay (no planned batches at all) */}
        {plannedBatchesFlat.length === 0 && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-white/40 text-[12px] text-text-sub-600">
            No planned batches to map.
          </div>
        )}

        {/* Floating rider sidebar */}
        <div className="absolute right-4 top-4 bottom-4 z-20 flex w-[380px] flex-col overflow-hidden rounded-lg border border-stroke-soft-200 bg-white shadow-xl">
          <div className="border-b border-stroke-soft-200 px-4 py-3">
            <div className="flex items-baseline justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-bold tracking-tight text-text-strong-950">
                  Planned · {shippingBatchesFlat.length} batch
                  {shippingBatchesFlat.length === 1 ? '' : 'es'}
                  {failedInfo.rows.length > 0 && (
                    <span className="ml-1.5 font-semibold text-red-600">
                      + {failedInfo.rows.length} gagal
                    </span>
                  )}
                </p>
                <p className="text-[11px] text-text-sub-600 truncate">
                  {hiddenRiderIds.size === 0
                    ? 'Click a rider to expand. Eye toggle hides their routes.'
                    : `${visiblePlannedBatches.length}/${plannedBatchesFlat.length} batches visible on map.`}
                </p>
              </div>
              {hiddenRiderIds.size > 0 && (
                <button
                  type="button"
                  onClick={() => setHiddenRiderIds(new Set())}
                  className="shrink-0 text-[11px] font-bold uppercase tracking-[0.14em] text-accent transition-colors hover:underline"
                >
                  Show all
                </button>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-3">
            {/* Held bucket first — the DEFERRED batch(es) sit at the top of
                the sidebar so ops sees held goods before scanning the routed
                riders below. */}
            {deferredInfo.batches.length > 0 && (
              <div className="mb-3">
                <DeferredSection
                  batches={deferredBatches}
                  editable={isEditable}
                  deferReasonLabel={deferReasonLabel}
                  onRequestAssign={(batch, stops) =>
                    setAssignDeferredTarget({ batch, stops })
                  }
                  busy={busy}
                />
              </div>
            )}
            {plannedByRider.length === 0 ? (
              <p className="px-2 text-[13px] text-text-sub-600">
                No planned batches. Assign one from the “Deferred (Ditahan)”
                panel above to ship items this run.
              </p>
            ) : (
              <div className="space-y-3">
                {plannedByRider.map((group) => {
                  const isHidden = hiddenRiderIds.has(group.rider.id);
                  const isExpanded = expandedRiderIds.has(group.rider.id);
                  const isSolo = soloRiderId === group.rider.id;
                  return (
                    <div
                      key={group.rider.id}
                      className={
                        isHidden ? 'opacity-50 transition-opacity' : undefined
                      }
                    >
                      {/* Rider header — accordion toggle. Click anywhere
                          (except the eye button) to expand/collapse and solo
                          the rider on the map. When collapsed, batch color
                          dots preview which routes this rider owns. */}
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleRiderExpanded(group.rider.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleRiderExpanded(group.rider.id);
                          }
                        }}
                        className={`cursor-pointer rounded-lg border px-2.5 py-2 transition-colors ${
                          isSolo
                            ? 'border-accent bg-bg-weak-50'
                            : 'border-stroke-soft-200 bg-bg-weak-50 hover:bg-bg-soft-200'
                        }`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRiderVisibility(group.rider.id);
                            }}
                            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-text-sub-600 transition-colors hover:bg-white hover:text-accent"
                            aria-label={
                              isHidden
                                ? `Show ${group.rider.name} on map`
                                : `Hide ${group.rider.name} from map`
                            }
                          >
                            <i
                              className={`${
                                isHidden ? 'ri-eye-off-line' : 'ri-eye-line'
                              } text-[15px]`}
                            />
                          </button>
                          <span className="block text-[13px] font-bold text-text-strong-950 tracking-tight truncate">
                            {group.rider.name}
                          </span>
                          <span className="font-mono text-[10px] text-text-sub-600 tabular-nums shrink-0">
                            {group.rider.code}
                          </span>
                          <div className="ml-auto flex items-center gap-2 shrink-0">
                            {isEditable && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setReassignRiderSource(group.rider);
                                }}
                                className="flex h-7 w-7 items-center justify-center rounded-full text-text-sub-600 transition-colors hover:bg-white hover:text-accent"
                                aria-label={`Reassign all of ${group.rider.name}'s batches`}
                                title="Transfer or swap all batches"
                              >
                                <i className="ri-arrow-left-right-line text-[15px]" />
                              </button>
                            )}
                            {!isExpanded && (
                              <div className="flex items-center gap-1">
                                {group.batches.map((batch) => {
                                  const idx = batchIndexById.get(batch.id);
                                  return (
                                    <span
                                      key={batch.id}
                                      className="h-2.5 w-2.5 rounded-full ring-1 ring-white"
                                      style={{
                                        backgroundColor:
                                          idx !== undefined
                                            ? batchColor(idx)
                                            : '#9ca3af',
                                      }}
                                      aria-hidden="true"
                                    />
                                  );
                                })}
                              </div>
                            )}
                            <i
                              className={`${
                                isExpanded
                                  ? 'ri-arrow-down-s-line'
                                  : 'ri-arrow-right-s-line'
                              } text-[16px] text-text-sub-600`}
                              aria-hidden="true"
                            />
                          </div>
                        </div>
                        <p className="mt-1.5 flex flex-wrap items-center gap-x-1 pl-9 text-[10px] text-text-sub-600 tabular-nums">
                          <span>
                            <strong className="text-text-strong-950">
                              {group.shippingBatchCount}
                            </strong>{' '}
                            trip{group.shippingBatchCount === 1 ? '' : 's'} ·{' '}
                            <strong className="text-text-strong-950">
                              {group.totalItems}
                            </strong>{' '}
                            items ·{' '}
                            <strong className="text-text-strong-950">
                              {fmtWeight(group.totalWeightKg)}
                            </strong>{' '}
                            ·{' '}
                            <strong className="text-text-strong-950">
                              {fmtMinutes(group.assignedTimeMinutes)}
                            </strong>{' '}
                            / {fmtMinutes(dispatch.shiftMinutes)}
                          </span>
                          {/* Rider-level roll-up of the batches' unscanned
                              items — visible while the group is collapsed. */}
                          <UnscannedBadge
                            count={group.unscannedItemCount}
                            size="sm"
                          />
                          {/* Bridge failure surfaced on the collapsed rider row
                              so ops doesn't have to expand every rider to find
                              which trip didn't go out. */}
                          {group.failedBatchCount > 0 && (
                            <span className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-1.5 py-0.5 text-[10px] font-bold text-red-700">
                              <i
                                className="ri-error-warning-line text-[11px]"
                                aria-hidden="true"
                              />
                              {group.failedBatchCount} trip gagal
                            </span>
                          )}
                        </p>
                      </div>
                      {isExpanded && (
                        <div className="mt-2 space-y-2">
                          <DndContext
                            sensors={dndSensors}
                            collisionDetection={closestCenter}
                            onDragEnd={(event: DragEndEvent) => {
                              const { active, over } = event;
                              if (!over || active.id === over.id) return;
                              const ids = group.batches.map((b) => b.id);
                              const from = ids.indexOf(String(active.id));
                              const to = ids.indexOf(String(over.id));
                              if (from === -1 || to === -1) return;
                              void applyTripReorder(
                                group.rider,
                                arrayMove(ids, from, to)
                              );
                            }}
                          >
                          <SortableContext
                            items={group.batches.map((b) => b.id)}
                            strategy={verticalListSortingStrategy}
                          >
                          {group.batches.map((batch) => {
                            const idx = batchIndexById.get(batch.id);
                            return (
                              <SortableBatchRow
                                key={batch.id}
                                batchId={batch.id}
                                showHandle={
                                  isEditable && group.batches.length > 1
                                }
                                disabled={busy != null}
                                innerRef={(el) => {
                                  if (el)
                                    batchRefsMap.current.set(batch.id, el);
                                  else batchRefsMap.current.delete(batch.id);
                                }}
                              >
                                {(dragHandle) => (
                                <BatchCard
                                  dragHandle={dragHandle}
                                  onReorderStops={
                                    isEditable
                                      ? (orderedStopIds) =>
                                          void applyStopReorder(
                                            batch,
                                            orderedStopIds
                                          )
                                      : undefined
                                  }
                                  batch={batch}
                                  capWeightKg={dispatch.maxWeightKg}
                                  capVolumeM3={dispatch.maxVolumeM3}
                                  busy={busy === batch.id}
                                  editable={isEditable}
                                  compact
                                  accentColor={
                                    idx !== undefined
                                      ? batchColor(idx)
                                      : undefined
                                  }
                                  selected={selectedBatchId === batch.id}
                                  onSelect={() =>
                                    setSelectedBatchId((curr) =>
                                      curr === batch.id ? null : batch.id
                                    )
                                  }
                                  onReassignBatch={() =>
                                    setReassignBatchTarget(batch)
                                  }
                                  onMergeTrip={
                                    plannedBatches.filter(
                                      (b) =>
                                        b.assignedRiderId ===
                                        batch.assignedRiderId
                                    ).length > 1
                                      ? () => setMergeTripSource(batch)
                                      : undefined
                                  }
                                  onStopReassign={(stopId) =>
                                    setReassignStopId(stopId)
                                  }
                                  onMoveStop={
                                    plannedBatches.filter(
                                      (b) =>
                                        b.assignedRiderId ===
                                        batch.assignedRiderId
                                    ).length > 1
                                      ? (stopId) => {
                                          const stop = batch.stops.find(
                                            (s) => s.id === stopId
                                          );
                                          if (stop)
                                            setMoveStopTarget({ stop, batch });
                                        }
                                      : undefined
                                  }
                                  onRemoveItem={(item) =>
                                    setCancelItemTarget(item)
                                  }
                                  onCancelStopItems={(stopId) =>
                                    setCancelStopId(stopId)
                                  }
                                />
                                )}
                              </SortableBatchRow>
                            );
                          })}
                          </SortableContext>
                          </DndContext>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Run summary (caps + time model) moved into the header "Run details"
          popover — it was reference data ops only checks occasionally, and
          living below a full-height map forced a scroll past the whole map to
          reach it. One click now, no scroll. */}

      {/* ── Modals ──────────────────────────────────────────────────── */}
      <ReassignRiderModal
        isOpen={!!reassignStopId}
        onClose={() => setReassignStopId(null)}
        stopId={reassignStopId}
        currentRiderId={reassignSourceRiderId}
        riders={dispatch.ridersSnapshot}
        onSubmit={applyStopReassign}
      />
      <ReassignBatchesModal
        isOpen={!!reassignRiderSource}
        onClose={() => setReassignRiderSource(null)}
        fromRider={reassignRiderSource}
        riders={dispatch.ridersSnapshot}
        onSubmit={applyRiderReassign}
      />
      <ReassignBatchModal
        isOpen={!!reassignBatchTarget}
        onClose={() => setReassignBatchTarget(null)}
        batch={reassignBatchTarget}
        riders={dispatch.ridersSnapshot}
        onSubmit={applyBatchReassign}
      />
      <AssignDeferredModal
        isOpen={!!assignDeferredTarget}
        onClose={() => setAssignDeferredTarget(null)}
        batch={assignDeferredTarget?.batch ?? null}
        stops={assignDeferredTarget?.stops ?? []}
        riders={dispatch.ridersSnapshot}
        onSubmit={async (stopIds, rider) => {
          if (!assignDeferredTarget) return;
          await applyStopsReassign(
            stopIds,
            rider,
            assignDeferredTarget.batch.id
          );
          setAssignDeferredTarget(null);
        }}
      />
      <MoveStopModal
        isOpen={!!moveStopTarget}
        onClose={() => setMoveStopTarget(null)}
        stop={moveStopTarget?.stop ?? null}
        sourceBatchId={moveStopTarget?.batch.id ?? null}
        riderName={moveStopTarget?.batch.assignedRiderName ?? null}
        // Same-rider trips only — a stop moves between its rider's own trips,
        // not onto another rider (that's the per-stop "Reassign" action).
        candidateBatches={
          moveStopTarget
            ? plannedBatches.filter(
                (b) =>
                  b.assignedRiderId === moveStopTarget.batch.assignedRiderId
              )
            : []
        }
        capWeightKg={dispatch.maxWeightKg}
        capVolumeM3={dispatch.maxVolumeM3}
        onSubmit={(toBatch) =>
          moveStopTarget
            ? applyStopMoveToBatch(moveStopTarget.stop.id, toBatch)
            : Promise.resolve()
        }
      />
      <MergeTripModal
        isOpen={!!mergeTripSource}
        onClose={() => setMergeTripSource(null)}
        sourceBatch={mergeTripSource}
        riderName={mergeTripSource?.assignedRiderName ?? null}
        candidateBatches={
          mergeTripSource
            ? plannedBatches.filter(
                (b) =>
                  b.assignedRiderId === mergeTripSource.assignedRiderId &&
                  b.id !== mergeTripSource.id
              )
            : []
        }
        capWeightKg={dispatch.maxWeightKg}
        capVolumeM3={dispatch.maxVolumeM3}
        onSubmit={(targetBatch) =>
          mergeTripSource
            ? applyMergeTrips(mergeTripSource, targetBatch)
            : Promise.resolve()
        }
      />
      <CancelStopItemsModal
        isOpen={!!cancelStopId}
        onClose={() => setCancelStopId(null)}
        stop={cancelStopTarget}
        onSubmit={applyStopDisposition}
      />
      <CancelItemModal
        isOpen={!!cancelItemTarget}
        onClose={() => setCancelItemTarget(null)}
        item={cancelItemTarget}
        onSubmit={applyItemDisposition}
      />
      <AddRidersModal
        isOpen={addRidersOpen}
        onClose={() => setAddRidersOpen(false)}
        existingRiders={dispatch.ridersSnapshot}
        hubName={hub?.name}
        onSubmit={applyAddRiders}
      />
      <ForceAssignModal
        isOpen={forceAssignOpen}
        onClose={() => setForceAssignOpen(false)}
        dispatchId={dispatch.id}
        // Only the dispatch's own riders — a forced batch is one more trip for
        // someone already on this run; new riders come in via Add riders.
        riders={dispatch.ridersSnapshot}
        tripCountByRiderId={Object.fromEntries(
          plannedByRider.map((g) => [g.rider.id, g.batches.length])
        )}
        onSubmit={applyForceAssign}
      />
      <RefreshDispatchModal
        isOpen={refreshPlanOpen}
        onClose={() => setRefreshPlanOpen(false)}
        submitting={refreshingPlan}
        onSubmit={applyRefreshPlan}
      />
      <CommitDispatchModal
        isOpen={commitOpen}
        onClose={() => {
          setCommitOpen(false);
          // Drop the gate rejection when ops leaves — it's re-derived on
          // the next commit attempt anyway.
          setUnscannedError(null);
        }}
        dispatch={dispatch}
        plannedRiderGroups={plannedByRider}
        submitting={committing}
        onSubmit={submitCommit}
        unscannedError={unscannedError}
      />
    </div>
  );
};

export default DispatchDetailPage;
